import { Seo } from './components/Seo';
import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigationType } from 'react-router-dom';

import { reloadIfReleasePending } from './lib/releaseWatcher';
import { Layout } from './components/layout/Layout';
import { HomePage } from './features/home/HomePage';
import { CatalogPage } from './features/catalog/CatalogPage';
const ProductDetailPage = lazy(() =>
  import('./features/products/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage })),
);
const CartPage = lazy(() =>
  import('./features/cart/CartPage').then((m) => ({ default: m.CartPage })),
);
const WishlistPage = lazy(() =>
  import('./features/wishlist/WishlistPage').then((m) => ({ default: m.WishlistPage })),
);
const AuthPage = lazy(() =>
  import('./features/auth/AuthPage').then((m) => ({ default: m.AuthPage })),
);
import { ForgotPasswordPage, ResetPasswordPage } from './features/auth/PasswordResetPages';
const VerifyEmailPage = lazy(() =>
  import('./features/auth/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
);
const CheckoutPage = lazy(() =>
  import('./features/checkout/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
const OrdersPage = lazy(() =>
  import('./features/orders/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('./features/orders/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })),
);
const StorePolicies = lazy(() =>
  import('./features/legal/StorePolicies').then((m) => ({ default: m.StorePolicies })),
);
const PrivacyPolicyPage = lazy(() =>
  import('./features/legal/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);

/**
 * Storefront routes (SRS §3.3). M4 browse, M5 cart/wishlist/auth, M6 checkout + orders.
 */
export default function App(): JSX.Element {
  // A new release detected mid-session hard-reloads on the next navigation (a safe point).
  const { pathname } = useLocation();
  const navigation = useNavigationType();
  useEffect(() => {
    if (navigation !== 'POP') window.scrollTo(0, 0);
  }, [pathname, navigation]);
  useEffect(() => reloadIfReleasePending(), [pathname]);

  return (
    <>
      <Seo />
      <Suspense
        fallback={
          <div className="mx-auto max-w-7xl p-6" role="status">
            Loading…
          </div>
        }
      >
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/products" element={<CatalogPage />} />
            <Route path="/category/:categorySlug" element={<CatalogPage />} />
            <Route path="/products/:slug" element={<ProductDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/wishlist" element={<WishlistPage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:idOrNumber" element={<OrderDetailPage />} />
            <Route path="/delivery" element={<StorePolicies kind="delivery" />} />
            <Route path="/returns" element={<StorePolicies kind="returns" />} />
            <Route path="/terms" element={<StorePolicies kind="terms" />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-4xl font-light text-navy-900">404</h1>
      <p className="mt-3 text-neutral">That page could not be found.</p>
    </div>
  );
}
